/**
 * rp-pdp-video — muted, in-viewport autoplay for PDP product video. U1.
 *
 * Client: "videos on pdp should autoplay, but don't let it affect performance
 * so non blocking or something?"
 *
 * This is spec 044 §6's own recommendation — Option B, "gated muted autoplay
 * with Option A's poster-and-button as the guaranteed fallback in every refused
 * case" — finally shipped. What was on the PDP until now was Option A: a poster
 * and a play button, and nothing ever moved. §6 was never wrong; it was never
 * built.
 *
 * ── WHAT "NON-BLOCKING" MEANS HERE, CONCRETELY ─────────────────────────────
 * 1. THERE IS NO <video> IN THE DOCUMENT AT REST. The player lives inside
 *    `<template data-rp-player>` and is cloned in only when this element is
 *    at least half in the viewport. `preload="none"` is on the element for the
 *    moment it exists, but the stronger guarantee is that until then there is
 *    no media element at all — 036 §4 measured that even `preload="none"`
 *    reserves media resources on iOS.
 * 2. THE RESTING STATE IS A REAL SHOPIFY IMAGE. `video.preview_image` through
 *    `image_tag` with the same `widths`/`sizes` every photo slide gets, so the
 *    frame is painted before a byte of video is requested. That poster is also
 *    the LCP candidate when the clip is media index 0 — never the video.
 * 3. PLAY IS SCHEDULED IN AN IDLE CALLBACK inside an IntersectionObserver
 *    callback. The observer cannot fire before layout, and the idle guard keeps
 *    the fetch out of the main thread's critical path — 044 §4 rule 3,
 *    "playback starts after the LCP has settled, never before".
 * 4. THE MODULE ITSELF IS CONDITIONAL. `snippets/scripts.liquid` emits the tag
 *    only on a product template whose product actually HAS a video — in its
 *    media or in `kdrams.pdp_standout`. A product without video ships zero
 *    bytes of this, which is 044 §4's "products without video ship nothing".
 * 5. ONE IntersectionObserver AND ONE `visibilitychange` LISTENER FOR THE PAGE,
 *    module-level, shared by every instance. 060 §6.3 caps the page at three
 *    observers; motion.js owns one and rp-card.js owns one, so this is the
 *    third and last.
 *
 * ── THE GATES, AND WHAT EACH ONE LANDS ON ──────────────────────────────────
 * Every refusal lands on the SAME state: the poster, painted, with a working
 * play button. That is not a degraded path, it is Option A, which shipped and
 * was reviewed.
 *
 *   html[data-motion="off"]        autoplay is motion. 060 §8 contract rule 7
 *                                  gates what STARTS, and this starts.
 *   navigator.connection.saveData  044 §4: "a shopper who has asked their
 *                                  browser not to spend data has asked us too".
 *   effectiveType 2g / slow-2g     same paragraph. ~1.5–3 MB is not a 2G asset.
 *   no IntersectionObserver        nothing to gate on, so nothing autoplays.
 *   play() rejected                iOS Low Power Mode, Data Saver, or the user
 *                                  setting. 044 §2: "autoplay can never be
 *                                  assumed to have happened". The catch puts
 *                                  the control back and leaves the poster up.
 *
 * ⚠️ NO prefers-reduced-motion MEDIA QUERY HERE. AGENTS.md caps the theme at
 * two detection sites — the inline script in layout/theme.liquid and
 * assets/motion.js — and both are spent. This reads the `data-motion` attribute
 * they write, exactly as assets/rp-pdp-gallery.js:114 does and for the same
 * stated reason. Reading published state is not detecting.
 *
 * ── WCAG ───────────────────────────────────────────────────────────────────
 * 2.2.2 Pause, Stop, Hide: the control is PERSISTENT while the clip plays and
 * becomes a pause button. Never hover-revealed (AGENTS.md §Accessibility), and
 * native `controls` stay on the element for the full keyboard set.
 * 1.4.2 Audio Control: `muted` is set in Liquid AND re-asserted on the element
 * before play(), because an autoplaying clip with sound fails outright — and
 * would not play anyway.
 * Focus is never moved by autoplay. It moves only when the shopper presses the
 * control, which is the one place a shopper expects it.
 */

/* ── shared page-level machinery ──────────────────────────────────────────── */

/** Every connected instance, so `visibilitychange` can reach all of them
 *  without a second observer or a document query. */
const hosts = new Set();

/** Half in view. A gallery slide scrolled out of the horizontal snap track
 *  reports 0 through the ancestor clip, so this doubles as 044 §3's
 *  "plays when the shopper swipes it into view" on mobile — for free, with no
 *  scroll listener, which blocks are forbidden from adding (060 rule 5). */
const io =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const host = entry.target;
            host.inView = entry.isIntersecting;
            if (entry.isIntersecting) host.schedule();
            else host.pause();
          }
        },
        { threshold: 0.5 }
      )
    : null;

/* A clip decoding in a background tab is the classic battery bug and it is
 * invisible in testing (044 §4 rule 5). One listener, not one per instance. */
document.addEventListener('visibilitychange', () => {
  for (const host of hosts) {
    if (document.hidden) host.pause();
    else if (host.inView && host.auto) host.schedule();
  }
});

/** requestIdleCallback where it exists, a macrotask where it does not (Safari
 *  <17). The timeout matters more than the API: it bounds how long the fetch
 *  can be deferred, so a busy main thread delays autoplay rather than
 *  cancelling it. */
const idle = (fn) =>
  'requestIdleCallback' in window
    ? requestIdleCallback(fn, { timeout: 500 })
    : setTimeout(fn, 0);

/** Reads state published by theme.liquid's inline script. Not a detection site. */
const motionOn = () => document.documentElement.dataset.motion !== 'off';

/** 044 §4's data-cost gate. `connection` is Chromium-only; its absence is not a
 *  refusal, because Safari and Firefox shoppers have no way to signal saveData
 *  and refusing them all would turn the feature off for ~30% of traffic. */
const dataOk = () => {
  const c = navigator.connection;
  if (!c) return true;
  if (c.saveData) return false;
  return !/2g/.test(c.effectiveType || '');
};

/* ── the element ──────────────────────────────────────────────────────────── */

class RpPdpVideo extends HTMLElement {
  connectedCallback() {
    // Re-read on every connect. The product section re-renders through the
    // Section Rendering API on a variant change and this element arrives with a
    // new template and a new poster; anything cached in a constructor would
    // point at the previous variant's clip. Same reasoning as
    // rp-pdp-gallery.js:26.
    this.tpl = this.querySelector('template[data-rp-player]');
    this.button = this.querySelector('[data-rp-toggle]');
    this.video = null;
    this.inView = false;
    if (!this.tpl || !this.button) return;

    // The click path is owned here rather than by rp-card.js's delegated
    // `[data-rp-play]` handler, which this markup used to borrow. Two owners
    // for one <template> is how you get a second cloned player: rp-card.js's
    // playVideo() calls stopVideo() and hides the button, this file toggles it.
    // The PDP control is `data-rp-toggle`, which rp-card.js does not match, so
    // there is exactly one owner on the page.
    this.onClick = () => this.toggle();
    this.button.addEventListener('click', this.onClick);

    this.auto = !!io && motionOn() && dataOk();
    if (this.auto) io.observe(this);
    hosts.add(this);
  }

  disconnectedCallback() {
    hosts.delete(this);
    if (io) io.unobserve(this);
    this.button?.removeEventListener('click', this.onClick);
    this.teardown();
  }

  /** Autoplay entry point. Never called from a click. */
  schedule() {
    if (!this.auto || this.pending) return;
    this.pending = true;
    idle(() => {
      this.pending = false;
      // Re-check rather than trust the callback: between the observer firing
      // and the idle slot the shopper may have swiped past, paused, or
      // backgrounded the tab.
      if (this.isConnected && this.inView && !document.hidden && this.auto) this.play();
    });
  }

  /** The control. A click is the shopper asking, so it ignores every gate
   *  except the browser's own — 044 §5 requires the opt-in path to survive
   *  reduced motion, and gating the click would remove it. */
  toggle() {
    if (this.video && !this.video.paused) {
      // An explicit pause must stick. Without this the next observer callback
      // would restart the clip the shopper just stopped, which reads as a
      // broken control rather than as autoplay.
      this.auto = false;
      this.pause();
    } else {
      this.play();
    }
  }

  play() {
    const video = this.mount();
    if (!video) return;
    video.play().then(
      () => this.mark(true),
      () => this.mark(false) // refused: poster stays, control stays a play button
    );
  }

  pause() {
    this.video?.pause();
    this.mark(false);
  }

  /** Clone the player in on first use. Idempotent — every later play() reuses
   *  the same element, so scrolling a clip in and out costs one fetch, not one
   *  per pass. */
  mount() {
    if (this.video) return this.video;
    this.appendChild(this.tpl.content.cloneNode(true));
    const video = this.querySelector('video');
    if (!video) return null;

    // `video_tag` emits neither of these. Without `playsinline` an autoplay
    // attempt takes over the screen on iOS instead of playing in the frame
    // (036 §2, and assets/rp-card.js:222 says the same from the card side);
    // `muted` is re-asserted because it is the precondition for the play()
    // promise resolving at all.
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.muted = true;

    this.video = video;
    return video;
  }

  teardown() {
    const video = this.video;
    if (!video) return;
    this.video = null;
    // pause → drop the source → load() → remove. A detached <video> still
    // holding its buffer is how a session ends up at hundreds of MB;
    // rp-card.js:257 pays the same care for the same reason.
    video.pause();
    video.removeAttribute('src');
    video.querySelectorAll('source').forEach((s) => s.remove());
    video.load();
    video.remove();
  }

  /** One place that writes the control's state, so the glyph, the accessible
   *  name and the CSS hook can never disagree. The labels are read off
   *  `data-*` attributes the snippet fills from `t` — no strings in JS. */
  mark(playing) {
    const b = this.button;
    if (!b) return;
    b.toggleAttribute('data-playing', playing);
    const label = playing ? b.dataset.labelPause : b.dataset.labelPlay;
    if (label) b.setAttribute('aria-label', label);
  }
}

if (!customElements.get('rp-pdp-video')) {
  customElements.define('rp-pdp-video', RpPdpVideo);
}
